// Extracted from original/src/components/InstallmentsView.tsx (section 7 "الأقساط والبيع بالتقسيط", ADR-08 §2.1
// "copy, then cut logic"):
//   top banner, quick buttons, six-tile numbers bar                           :1381-1477
//   sub-tabs (contracts / new / alerts)                                       :1479-1524
//   alerts tab: header, empty state, due table with pay / WhatsApp / view    :2459-2622
//   contracts tab: search, list print capsule, status tabs, date range, grid :2625-2856
//   list print / PDF / WhatsApp capsule (`ContractsListPrintShareCapsule`)    :231-269
//   per-contract capsule (`PrintShareCapsule`)                                 :149-187
// Kept: every class string, icon and size, the gradient banner, the tile order and labels, the card layout (contract
// badge, customer, phone, status pill with the pulse on a late contract, item / total strip, progress bar, frequency and
// instalment amount, footer actions), the alerts table columns, the empty states and their buttons.
// Cut: `localDataService.getInstallmentContracts()` and every figure the old view computed in the browser
// (`duePaymentsList`, `filteredContracts`' status test, `totalWithProfit*`, `veryOverdueCount`, UTC `todayStr`,
// R-2 / R-11 / F-147) → the server's read model `GET /installments` (D-109 (1): contracts are server records; due /
// overdue in office dates, D-107), passed in as preformatted labels; the delete handler with the super code / "2"
// (R-9, F-157) → `onCancel` (reason + personal PIN, D-109 (4), D-018).
// Changed by decision: the status pill also shows a contract waiting for approval / rejected / cancelled (D-035,
// D-109 (4)); the paid figure on a card is the down payment + the active payments (R-14, D-109 (6)); the "new contract"
// buttons and the trash icon follow the D-109 (5) tokens; the list capsule prints the A4 list through the container.
// Added: data-testid attributes.
import type { ReactNode } from 'react';
import { AlertTriangle, Bell, Calendar, CheckCircle2, Clock, CreditCard, FileText, Package, Percent, Phone, Plus, Printer, Search, Send, Share2, Trash2, User } from 'lucide-react';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';

export type InstallmentsTab = 'contracts' | 'new' | 'alerts';
export type InstallmentsStatusFilter = 'all' | 'active' | 'completed';
export type InstallmentsCardStatus = 'active' | 'late' | 'completed' | 'pending' | 'rejected' | 'withdrawn' | 'cancelled';

/** Per-currency tile figure: IQD / USD labels, null when that currency is zero (old tiles hid a zero line). */
export interface InstallmentsMoneyTile {
  readonly iqd: string | null;
  readonly usd: string | null;
}

export interface InstallmentsTiles {
  readonly contracts: number;
  readonly active: number;
  readonly withProfit: InstallmentsMoneyTile;
  readonly withoutProfit: InstallmentsMoneyTile;
  readonly due: number;
  readonly veryOverdue: number;
}

export interface InstallmentsAlertRow {
  readonly key: string;
  readonly contractId: string;
  readonly customer: string;
  readonly phone: string | null;
  readonly itemName: string;
  readonly contractNumber: string;
  readonly rowNo: number;
  readonly rowCount: number;
  readonly dueDate: string;
  /** Amount still required on the row, with the currency (server figure). */
  readonly openLabel: string;
  readonly today: boolean;
  /** Days after the due date (office dates); 0 for today. */
  readonly daysLate: number;
  /** wa.me link with the reminder text, or null without a phone. */
  readonly whatsappUrl: string | null;
}

export interface InstallmentsCard {
  readonly id: string;
  readonly contractNumber: string;
  readonly customer: string;
  readonly phone: string | null;
  readonly status: InstallmentsCardStatus;
  readonly dateLabel: string;
  readonly itemName: string;
  readonly totalLabel: string;
  readonly paidLabel: string;
  readonly progressPct: number;
  readonly periodLabel: string;
  readonly installmentLabel: string;
  readonly canCancel: boolean;
}

export interface InstallmentsProps {
  readonly tab: InstallmentsTab;
  readonly onTab: (tab: InstallmentsTab) => void;
  readonly tiles: InstallmentsTiles;
  readonly contractsCount: number;
  readonly canCreate: boolean;
  readonly canCollect: boolean;
  /** The new-contract form (InstallmentsModals `NewContractForm`), rendered on the "new" tab. */
  readonly newForm: ReactNode;
  readonly alerts: readonly InstallmentsAlertRow[];
  readonly onPayAlert: (row: InstallmentsAlertRow) => void;
  readonly onView: (contractId: string) => void;
  // contracts tab
  readonly search: string;
  readonly onSearch: (v: string) => void;
  readonly status: InstallmentsStatusFilter;
  readonly onStatus: (v: InstallmentsStatusFilter) => void;
  readonly counts: { readonly all: number; readonly active: number; readonly completed: number };
  readonly fromDate: string;
  readonly toDate: string;
  readonly onFromDate: (v: string) => void;
  readonly onToDate: (v: string) => void;
  readonly cards: readonly InstallmentsCard[];
  readonly onPrintList: () => void;
  readonly onPdfList: () => void;
  readonly onShareList: () => void;
  readonly onPrint: (contractId: string) => void;
  readonly onPdf: (contractId: string) => void;
  readonly onShare: (contractId: string) => void;
  readonly onCancel: (contractId: string) => void;
  /** Overlays (detail, payment, cancel dialogs, print root). */
  readonly children?: ReactNode;
}

const capsuleBtn = 'p-1 text-emerald-700 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-200 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/40 rounded-full transition-all cursor-pointer';

/** The old `PrintShareCapsule` (IV:149-187) and `ContractsListPrintShareCapsule` (IV:231-269). */
export function PrintShareCapsule({ onPrint, onPdf, onShare, titles, testId }: { onPrint: () => void; onPdf: () => void; onShare: () => void; titles: { print: string; pdf: string; share: string }; testId?: string }) {
  return (
    <div className="flex items-center gap-3 bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 px-3.5 py-1.5 rounded-full select-none shadow-xs shrink-0 inline-flex" data-testid={testId}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onPrint(); }} className={capsuleBtn} title={titles.print} data-testid={testId ? `${testId}-print` : undefined}>
        <Printer size={16} />
      </button>
      <button type="button" onClick={(e) => { e.stopPropagation(); onPdf(); }} className={capsuleBtn} title={titles.pdf} data-testid={testId ? `${testId}-pdf` : undefined}>
        <FileText size={16} />
      </button>
      <button type="button" onClick={(e) => { e.stopPropagation(); onShare(); }} className={capsuleBtn} title={titles.share} data-testid={testId ? `${testId}-share` : undefined}>
        <Share2 size={16} />
      </button>
    </div>
  );
}

function MoneyTile({ v, tone }: { v: InstallmentsMoneyTile; tone: string }) {
  const { t } = useI18n();
  return (
    <div className="space-y-0.5">
      {v.iqd !== null && (
        <div className={`text-sm font-black ${tone}`}>
          {v.iqd} <span className="text-[10px] font-normal text-blue-100">{t('in.iqd_short')}</span>
        </div>
      )}
      {v.usd !== null && (
        <div className={`text-sm font-black ${tone}`}>
          {v.usd} <span className="text-[10px] font-normal text-blue-100">$</span>
        </div>
      )}
      {v.iqd === null && v.usd === null && <span className="text-sm font-black text-white">0</span>}
    </div>
  );
}

export function Installments(p: InstallmentsProps) {
  const { t } = useI18n();
  const contractTitles = { print: t('in.capsule_print'), pdf: t('in.capsule_pdf'), share: t('in.capsule_share') };
  const listTitles = { print: t('in.list_capsule_print'), pdf: t('in.list_capsule_pdf'), share: t('in.list_capsule_share') };
  const statusPill = (s: InstallmentsCardStatus): { cls: string; label: string } => {
    switch (s) {
      case 'completed':
        return { cls: 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300', label: t('in.status_completed') };
      case 'late':
        return { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 animate-pulse', label: t('in.status_late') };
      case 'pending':
        return { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300', label: t('in.status_pending') };
      case 'rejected':
        return { cls: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300', label: t('in.status_rejected') };
      case 'withdrawn':
        return { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', label: t('in.status_withdrawn') };
      case 'cancelled':
        return { cls: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400 line-through', label: t('in.status_cancelled') };
      default:
        return { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300', label: t('in.status_active') };
    }
  };

  return (
    <div className="space-y-6 pb-12" data-testid="installments-screen">
      {/* Top Banner / Stats Header */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-bold backdrop-blur-md flex items-center gap-1">
                <Percent size={14} /> {t('in.banner_badge')}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black">{t('in.banner_title')}</h1>
            <p className="text-blue-100 text-xs md:text-sm mt-1">{t('in.banner_sub')}</p>
          </div>

          {/* Quick Buttons */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            {p.canCreate && (
              <button
                onClick={() => p.onTab('new')}
                data-testid="in-new-quick"
                className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-emerald-500/25 active:scale-95 cursor-pointer text-sm"
              >
                <Plus size={18} /> {t('in.new_contract_btn')}
              </button>
            )}
            <button
              onClick={() => p.onTab('alerts')}
              data-testid="in-alerts-quick"
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all border border-white/20 backdrop-blur-md cursor-pointer text-sm ${
                p.tab === 'alerts' ? 'bg-white text-blue-900 shadow-lg' : 'bg-white/15 hover:bg-white/25 text-white'
              }`}
            >
              <Bell size={18} /> {t('in.alerts_btn', { n: p.alerts.length })}
            </button>
          </div>
        </div>

        {/* Quick Numbers Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mt-6 pt-4 border-t border-white/15 relative z-10" data-testid="in-tiles">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1">{t('in.tile_contracts')}</span>
            <span className="text-xl font-black">{t('in.n_contracts', { n: p.tiles.contracts })}</span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1">{t('in.tile_active')}</span>
            <span className="text-xl font-black text-emerald-300">{t('in.n_contracts', { n: p.tiles.active })}</span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1 font-sans">{t('in.tile_with_profit')}</span>
            <MoneyTile v={p.tiles.withProfit} tone="text-emerald-300" />
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1 font-sans">{t('in.tile_without_profit')}</span>
            <MoneyTile v={p.tiles.withoutProfit} tone="text-blue-200" />
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1">{t('in.tile_due')}</span>
            <span className="text-xl font-black text-amber-300">{t('in.n_rows', { n: p.tiles.due })}</span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 flex flex-col justify-between">
            <span className="text-xs text-blue-100 font-semibold block mb-1">{t('in.tile_very_overdue')}</span>
            <span className="text-xl font-black text-rose-300">{t('in.n_rows', { n: p.tiles.veryOverdue })}</span>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => p.onTab('contracts')}
          data-testid="in-tab-contracts"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs md:text-sm font-bold transition-all cursor-pointer ${
            p.tab === 'contracts' ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <FileText size={16} /> {t('in.tab_contracts', { n: p.contractsCount })}
        </button>
        {p.canCreate && (
          <button
            onClick={() => p.onTab('new')}
            data-testid="in-tab-new"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs md:text-sm font-bold transition-all cursor-pointer ${
              p.tab === 'new' ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Plus size={16} /> {t('in.tab_new')}
          </button>
        )}
        <button
          onClick={() => p.onTab('alerts')}
          data-testid="in-tab-alerts"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs md:text-sm font-bold transition-all cursor-pointer ${
            p.tab === 'alerts' ? 'bg-white dark:bg-gray-900 text-amber-600 dark:text-amber-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Bell size={16} /> {t('in.tab_alerts')}
          {p.alerts.length > 0 && <span className="bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-black">{p.alerts.length}</span>}
        </button>
      </div>

      {/* SUB-TAB 1: NEW CONTRACT FORM */}
      {p.tab === 'new' && p.newForm}

      {/* SUB-TAB: ALERTS & DUE INSTALLMENTS TABLE */}
      {p.tab === 'alerts' && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm space-y-6" data-testid="in-alerts">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded-2xl border border-amber-200 dark:border-amber-800/40 shrink-0">
                <Bell size={22} />
              </div>
              <div>
                <h2 className="text-lg font-black text-gray-900 dark:text-white">{t('in.alerts_title')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('in.alerts_sub')}</p>
              </div>
            </div>
            {p.alerts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/50 px-3.5 py-1.5 rounded-xl flex items-center gap-1.5">
                  <AlertTriangle size={14} className="text-amber-500" />
                  {t('in.alerts_total', { n: p.alerts.length })}
                </span>
              </div>
            )}
          </div>

          {p.alerts.length === 0 ? (
            <div className="py-20 text-center space-y-3" data-testid="in-alerts-empty">
              <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 flex items-center justify-center mx-auto border border-emerald-200 dark:border-emerald-800/50 shadow-inner">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-base font-black text-gray-900 dark:text-white">{t('in.alerts_empty_title')}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('in.alerts_empty_sub')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
                <table className="w-full text-right text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 font-black border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="p-3.5">{t('in.col_customer')}</th>
                      <th className="p-3.5">{t('in.col_contract_item')}</th>
                      <th className="p-3.5">{t('in.col_row')}</th>
                      <th className="p-3.5">{t('in.col_due_date')}</th>
                      <th className="p-3.5">{t('in.col_required')}</th>
                      <th className="p-3.5">{t('in.col_due_state')}</th>
                      <th className="p-3.5 text-center">{t('in.col_actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-bold">
                    {p.alerts.map((a) => (
                      <tr key={a.key} className="hover:bg-amber-50/40 dark:hover:bg-amber-950/20 transition-colors" data-testid="in-alert-row">
                        <td className="p-3.5">
                          <div className="font-black text-gray-900 dark:text-white flex items-center gap-1.5">
                            <User size={14} className="text-blue-500" />
                            {a.customer}
                          </div>
                          {a.phone && (
                            <div className="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                              <Phone size={11} />
                              {a.phone}
                            </div>
                          )}
                        </td>
                        <td className="p-3.5">
                          <div className="text-gray-900 dark:text-gray-100 font-bold">{a.itemName}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{t('in.contract_hash', { n: a.contractNumber })}</div>
                        </td>
                        <td className="p-3.5">
                          <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-lg text-[11px] font-bold">{t('in.row_of', { n: a.rowNo, m: a.rowCount })}</span>
                        </td>
                        <td className="p-3.5 font-mono text-gray-700 dark:text-gray-300">{a.dueDate}</td>
                        <td className="p-3.5">
                          <span className="font-mono font-black text-sm text-emerald-600 dark:text-emerald-400">{a.openLabel}</span>
                        </td>
                        <td className="p-3.5">
                          {a.today ? (
                            <span className="bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-200 dark:border-amber-800 px-2.5 py-1 rounded-full text-[11px] font-black inline-flex items-center gap-1">
                              <Clock size={12} /> {t('in.due_today')}
                            </span>
                          ) : (
                            <span className="bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300 border border-rose-200 dark:border-rose-800 px-2.5 py-1 rounded-full text-[11px] font-black inline-flex items-center gap-1">
                              <AlertTriangle size={12} /> {t('in.late_days', { n: a.daysLate })}
                            </span>
                          )}
                        </td>
                        <td className="p-3.5">
                          <div className="flex items-center justify-center gap-2">
                            {p.canCollect && (
                              <button
                                type="button"
                                onClick={() => p.onPayAlert(a)}
                                data-testid="in-alert-pay"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-xl text-xs font-black transition-all shadow-sm cursor-pointer flex items-center gap-1"
                                title={t('in.pay_now_title')}
                              >
                                <CreditCard size={13} /> {t('in.pay')}
                              </button>
                            )}
                            {a.whatsappUrl && (
                              <a
                                href={a.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="in-alert-whatsapp"
                                className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer flex items-center justify-center"
                                title={t('in.remind_whatsapp')}
                              >
                                <Send size={14} />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => p.onView(a.contractId)}
                              className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer"
                              title={t('in.view_title')}
                            >
                              {t('in.view')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SUB-TAB 2: CONTRACTS LIST & MANAGE */}
      {p.tab === 'contracts' && (
        <div className="space-y-4" data-testid="in-contracts">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="relative w-full md:w-80 lg:w-96">
                <Search className="absolute right-3.5 top-3.5 text-gray-400" size={18} />
                <input
                  type="text"
                  value={p.search}
                  onChange={(e) => p.onSearch(e.target.value)}
                  placeholder={t('in.search_ph')}
                  data-testid="in-search"
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pr-10 pl-4 py-2.5 text-xs font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              {p.cards.length > 0 ? (
                <div className="flex items-center justify-center animate-fade-in">
                  <PrintShareCapsule onPrint={p.onPrintList} onPdf={p.onPdfList} onShare={p.onShareList} titles={listTitles} testId="in-list-capsule" />
                </div>
              ) : (
                <div className="hidden md:block w-4" />
              )}

              <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl text-xs font-bold w-full md:w-auto overflow-x-auto">
                <button onClick={() => p.onStatus('all')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${p.status === 'all' ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>
                  {t('in.filter_all', { n: p.counts.all })}
                </button>
                <button onClick={() => p.onStatus('active')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${p.status === 'active' ? 'bg-white dark:bg-gray-900 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>
                  {t('in.filter_active', { n: p.counts.active })}
                </button>
                <button onClick={() => p.onStatus('completed')} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${p.status === 'completed' ? 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>
                  {t('in.filter_completed', { n: p.counts.completed })}
                </button>
              </div>
            </div>

            {/* Date Range Filter Row */}
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 dark:border-gray-800/60 pt-3 text-xs font-bold text-gray-700 dark:text-gray-300">
              <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <Calendar size={15} className="text-blue-500" />
              </span>
              <div className="flex items-center gap-2">
                <div className="w-40">
                  <EnglishDatePicker value={p.fromDate} onChange={p.onFromDate} className="!py-2 !text-xs !bg-white dark:!bg-gray-900" />
                </div>
                <span className="text-gray-400 font-normal">{t('in.to')}</span>
                <div className="w-40">
                  <EnglishDatePicker value={p.toDate} onChange={p.onToDate} className="!py-2 !text-xs !bg-white dark:!bg-gray-900" />
                </div>
              </div>
              {(p.fromDate || p.toDate) && (
                <button
                  onClick={() => {
                    p.onFromDate('');
                    p.onToDate('');
                  }}
                  className="px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-900/30 text-rose-600 dark:text-rose-400 transition-colors cursor-pointer"
                >
                  {t('in.clear_dates')}
                </button>
              )}
            </div>
          </div>

          {/* Contracts Grid */}
          {p.cards.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center space-y-3" data-testid="in-empty">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 text-gray-400 rounded-full flex items-center justify-center mx-auto">
                <FileText size={32} />
              </div>
              <h3 className="text-base font-black text-gray-800 dark:text-gray-200">{t('in.empty_title')}</h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">{t('in.empty_sub')}</p>
              {p.canCreate && (
                <button onClick={() => p.onTab('new')} className="mt-2 inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:bg-blue-700 transition-all cursor-pointer">
                  <Plus size={16} /> {t('in.empty_new')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {p.cards.map((c) => {
                const pill = statusPill(c.status);
                const late = c.status === 'late';
                return (
                  <div
                    key={c.id}
                    data-testid="in-card"
                    className={`bg-white dark:bg-gray-900 rounded-2xl border transition-all duration-200 p-5 space-y-4 hover:shadow-md relative overflow-hidden ${
                      late ? 'border-amber-300 dark:border-amber-800/60 ring-1 ring-amber-500/20' : 'border-gray-200 dark:border-gray-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2.5 py-1 rounded-md mb-1 inline-block">{t('in.contract_label', { n: c.contractNumber })}</span>
                        <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-1.5">
                          <User size={16} className="text-gray-400 shrink-0" /> {c.customer}
                        </h3>
                        {c.phone && (
                          <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 dir-ltr justify-end">
                            <Phone size={12} /> {c.phone}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[11px] font-black px-2.5 py-1 rounded-full ${pill.cls}`} data-testid="in-card-status">{pill.label}</span>
                        <span className="text-[10px] text-gray-400">{c.dateLabel}</span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3 flex items-center justify-between text-xs">
                      <span className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <Package size={14} className="text-emerald-500" /> {c.itemName}
                      </span>
                      <span className="font-black text-gray-900 dark:text-white">{c.totalLabel}</span>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px] font-bold">
                        <span className="text-gray-500">{t('in.paid_label', { v: c.paidLabel })}</span>
                        <span className="text-blue-600 dark:text-blue-400">{c.progressPct}%</span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-500 ${c.status === 'completed' ? 'bg-purple-500' : 'bg-emerald-500'}`} style={{ width: `${c.progressPct}%` }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px] pt-1 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="text-gray-400 block">{t('in.card_period')}</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{c.periodLabel}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">{t('in.card_installment')}</span>
                        <span className="font-black text-emerald-600 dark:text-emerald-400">{c.installmentLabel}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-2">
                      <button
                        onClick={() => p.onView(c.id)}
                        data-testid="in-card-view"
                        className="flex-1 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <Calendar size={14} /> {t('in.view_update')}
                      </button>
                      <PrintShareCapsule onPrint={() => p.onPrint(c.id)} onPdf={() => p.onPdf(c.id)} onShare={() => p.onShare(c.id)} titles={contractTitles} />
                      {c.canCancel && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            p.onCancel(c.id);
                          }}
                          data-testid="in-card-cancel"
                          className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl transition-all cursor-pointer"
                          title={t('in.cancel_contract')}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {p.children}
      {/* The old root's last child is the hidden list print area (IV:3428 `hidden print:block`); with Tailwind v4 `space-y-6`
          the last visible tab keeps its 24 px bottom margin because of it — kept for the exact page height. The list print
          itself is the container's StatementA4. */}
      <div className="hidden" aria-hidden="true" />
    </div>
  );
}
